import { LitElement, html, css } from 'lit';
import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import { findAncestor, parseVec3, ReadyMixin } from './base.js';

// <rapier-physics gravity="0 -22 0" container="0.4 12" flat count="50" drop-height="20">
// Owns the Rapier World, parents into the nearest <three-scene>, steps from its frame
// loop (always running). container="halfDepth wallHalfHeight". Bricks are STREAMED in
// (Datastar SSE patches <physics-body> into this element) and dropped the moment each one
// registers — no pre-rendered bricks, no client stagger (the server paces arrivals). The
// box width + a STATIC camera are sized once from `count`. Click a brick to re-drop it;
// hover highlights it.
export class RapierPhysics extends ReadyMixin(LitElement) {
  static properties = {
    gravity: { type: String },
    container: { type: String },
    flat: { type: Boolean },
    count: { type: Number },
    dropHeight: { type: Number, attribute: 'drop-height' },
  };
  static styles = css`
    :host {
      display: contents;
    }
  `;

  constructor() {
    super();
    this.gravity = '0 -22 0';
    this.container = '';
    this.flat = false;
    this.count = 40;
    this.dropHeight = 10;
    this.world = null;
    this.RAPIER = null;
    this._bodies = new Set();
    this._sceneEl = null;
    this._cb = null;
    this._HW = 8;
    this._HD = 1.2;
    this._FH = 5;
    this._walls = {};
    this._hovered = null;
  }

  register(b) {
    this._bodies.add(b);
    // streamed in → drop it as soon as it registers (the server already paces arrivals)
    if (this.world && this._cb) this._drop(b);
  }

  unregister(b) {
    this._bodies.delete(b);
    if (this._hovered === b) this._hovered = null;
  }

  async connectedCallback() {
    super.connectedCallback();
    await RAPIER.init();
    if (!this.isConnected) return;
    const [gx, gy, gz] = parseVec3(this.gravity, [0, -22, 0]);
    this.world = new RAPIER.World({ x: gx, y: gy, z: gz });
    this.RAPIER = RAPIER;
    this._markReady();

    const sceneEl = findAncestor(
      this,
      (c) => c.tagName === 'THREE-SCENE' || (c.object3D && typeof c.addFrameCallback === 'function'),
    );
    this._sceneEl = sceneEl;
    if (sceneEl?.whenReady?.then) await sceneEl.whenReady;
    if (!this.isConnected) return;

    if (this.container) this._buildContainer();
    this._sizeFromCount();

    const renderer = this._sceneEl?.getRenderer?.();
    if (renderer) {
      this._onDownBound = (e) => this._onPointerDown(e);
      this._onMoveBound = (e) => this._onPointerMove(e);
      renderer.domElement.addEventListener('pointerdown', this._onDownBound);
      renderer.domElement.addEventListener('pointermove', this._onMoveBound);
    }

    // world runs continuously; bricks fall as they stream in and register
    this._cb = () => {
      this.world.step();
      const HW = this._HW, HD = this._HD;
      for (const b of this._bodies) {
        b._sync();
        // escaped the box (tunneled a wall / fell through the floor)? re-drop from the top
        if (b.type === 'fixed' || !b._rb) continue;
        const t = b._rb.translation();
        if (t.y < -8 || Math.abs(t.x) > HW + 5 || Math.abs(t.z) > HD + 6) this._drop(b);
      }
    };
    this._sceneEl?.addFrameCallback?.(this._cb);
  }

  _buildContainer() {
    const parts = parseVec3(this.container, [1.2, 5, 0]);
    this._HD = parts[0] || 1.2;
    this._FH = parts[1] || 5;
    const R = this.RAPIER;
    const world = this.world;
    // wide fixed floor; side walls reposition to the box edges
    const floorRb = world.createRigidBody(R.RigidBodyDesc.fixed().setTranslation(0, -0.2, 0));
    world.createCollider(R.ColliderDesc.cuboid(80, 0.2, this._HD).setFriction(0.95), floorRb);
    this._walls.left = world.createRigidBody(R.RigidBodyDesc.fixed());
    world.createCollider(R.ColliderDesc.cuboid(0.4, this._FH, this._HD), this._walls.left);
    this._walls.right = world.createRigidBody(R.RigidBodyDesc.fixed());
    world.createCollider(R.ColliderDesc.cuboid(0.4, this._FH, this._HD), this._walls.right);
    // front + back walls — z-limits of the box; thin depth keeps bricks forward-facing
    this._walls.front = world.createRigidBody(R.RigidBodyDesc.fixed().setTranslation(0, this._FH, this._HD + 0.2));
    world.createCollider(R.ColliderDesc.cuboid(80, this._FH, 0.2), this._walls.front);
    this._walls.back = world.createRigidBody(R.RigidBodyDesc.fixed().setTranslation(0, this._FH, -this._HD - 0.2));
    world.createCollider(R.ColliderDesc.cuboid(80, this._FH, 0.2), this._walls.back);

    // box is invisible — floor/walls are colliders only (no mesh)
    this._ro = new ResizeObserver(() => this._sizeFromCount());
    this._ro.observe(this._sceneEl);
  }

  // Size the box so bricks fill the FULL viewport width — walls sit at the camera's visible
  // left/right edges (the magenta scene bounds), so the pile touches the side walls. Frames
  // a STATIC camera once (no per-frame motion). Visible height fits the pile + drop headroom.
  _sizeFromCount() {
    const cam = this._sceneEl?.getActiveCamera?.() || this._sceneEl?._defaultCamera;
    const renderer = this._sceneEl?.getRenderer?.();
    if (!cam || !renderer || !this._walls.left) return;
    const size = renderer.getSize(new THREE.Vector2());
    const aspect = size.x / Math.max(1, size.y);
    const area = (Number(this.count) || 40) * 2.2; // est. footprint per brick
    const vh = Math.max(6, Math.sqrt((1.4 * area) / aspect)); // visible height (pile + headroom)
    const HW = (vh * aspect) / 2; // walls at the visible horizontal edges → bricks touch them
    this._HW = HW;
    this._walls.left.setTranslation({ x: -HW, y: this._FH, z: 0 }, true);
    this._walls.right.setTranslation({ x: HW, y: this._FH, z: 0 }, true);
    const tanF = Math.tan(((cam.fov || 60) * Math.PI) / 360);
    const cy = vh / 2 - 0.2; // floor at the bottom edge
    cam.position.set(0, cy, vh / (2 * tanF) + this._HD);
    cam.lookAt(0, cy, 0);
  }

  // (re)launch one brick from the top at a random x with a small tumble
  _drop(b) {
    if (!b?._rb) return;
    if (b._obj) b._obj.visible = true;
    const span = Math.max(1, this._HW - 1.3);
    const x = (Math.random() * 2 - 1) * span;
    const q = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(
        (Math.random() * 2 - 1) * 0.25,
        (Math.random() * 2 - 1) * 0.25,
        (Math.random() * 2 - 1) * 0.4,
      ),
    );
    b._rb.setTranslation({ x, y: this.dropHeight, z: 0 }, true);
    b._rb.setRotation({ x: q.x, y: q.y, z: q.z, w: q.w }, false);
    b._rb.setLinvel({ x: 0, y: 0, z: 0 }, false);
    b._rb.setAngvel({ x: 0, y: 0, z: (Math.random() * 2 - 1) * 0.8 }, false);
    b._rb.setGravityScale(1, true);
    b._rb.wakeUp?.();
  }

  // raycast the pointer against the brick meshes → the body under the cursor (or null)
  _pick(ev) {
    const cam = this._sceneEl?.getActiveCamera?.();
    const renderer = this._sceneEl?.getRenderer?.();
    if (!cam || !renderer) return null;
    const rect = renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((ev.clientX - rect.left) / rect.width) * 2 - 1,
      -((ev.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this._ray = this._ray || new THREE.Raycaster();
    this._ray.setFromCamera(ndc, cam);
    const meshes = [];
    const map = (this._map = this._map || new Map());
    map.clear();
    for (const b of this._bodies) {
      if (b.type === 'fixed' || !b._obj) continue;
      meshes.push(b._obj);
      map.set(b._obj, b);
    }
    const hits = this._ray.intersectObjects(meshes, false);
    return hits.length ? map.get(hits[0].object) : null;
  }

  _onPointerDown(ev) {
    const b = this._pick(ev);
    if (b) this._drop(b);
  }

  _onPointerMove(ev) {
    const b = this._pick(ev);
    if (b === this._hovered) return;
    if (this._hovered) this._setHover(this._hovered, false);
    this._hovered = b;
    if (b) this._setHover(b, true);
    const canvas = this._sceneEl?.getRenderer?.()?.domElement;
    if (canvas) canvas.style.cursor = b ? 'pointer' : '';
  }

  _setHover(b, on) {
    const mesh = b?._obj;
    if (!mesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) if (m?.emissive) m.emissive.setHex(on ? 0x555555 : 0x000000);
    mesh.scale.setScalar(on ? 1.12 : 1);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._cb) this._sceneEl?.removeFrameCallback?.(this._cb);
    this._ro?.disconnect();
    const canvas = this._sceneEl?.getRenderer?.()?.domElement;
    if (canvas) {
      if (this._onDownBound) canvas.removeEventListener('pointerdown', this._onDownBound);
      if (this._onMoveBound) canvas.removeEventListener('pointermove', this._onMoveBound);
    }
  }

  render() {
    return html`<slot></slot>`;
  }
}

customElements.define('rapier-physics', RapierPhysics);
